import { DocumentV2, RotateImageRequest, videoTypeRegex } from '@eigtech/documents-types'
import { ensureDocumentCorn } from '@eigtech/documents-util'
import { useMutationState } from '@eigtech/ui-shared-api'
import { useGetDocumentUrl } from '@eigtech/ui-shared-documents'
import { Skeleton } from '@eigtech/ui/components/skeleton'
import { cn } from '@eigtech/ui/lib/utils'
import { useEffect, useState } from 'react'

export function MediaPreview({
  className: propsClassName,
  document,
  style,
}: {
  className?: string
  document: DocumentV2
  style?: React.CSSProperties
}) {
  const { id, entityId, filename } = document
  const {
    data: url,
    isPending: isPendingUrl,
    isFetching,
  } = useGetDocumentUrl({ entityId, documentId: id })

  const [loaded, setLoaded] = useState(false)

  const mutation = useMutationState({
    filters: {
      mutationKey: ['rotateImage'],
      exact: true,
      predicate(mutation) {
        const variables = mutation.state.variables as RotateImageRequest | undefined
        return (
          variables?.documentCorn === ensureDocumentCorn(document.id) &&
          variables?.entityId === entityId
        )
      },
    },
  })

  const isPending = mutation.some(({ status }) => status === 'pending')
  const isError = mutation.some(({ status }) => status === 'error')

  useEffect(() => {
    if (isError) {
      setLoaded(true)
    }
  }, [isError])

  useEffect(() => {
    if (isFetching || isPending) {
      setLoaded(false)
    }
  }, [isFetching, isPending])

  // This prevents a weird bug where if a carousel has a video
  // the video would always display irregardless of what slide
  // was first and could only be fixed by going backwards then
  // forwards in the carousel. No idea why.
  useEffect(() => {
    if (filename.match(videoTypeRegex) && !isPendingUrl) {
      setTimeout(() => {
        setLoaded(true)
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingUrl])

  return (
    <>
      {filename.match(videoTypeRegex) ? (
        !isPendingUrl &&
        loaded && (
          <video className={cn(className, propsClassName)} controls src={url} style={style} />
        )
      ) : (
        <>
          <img
            className={cn(className, propsClassName)}
            src={url}
            style={style}
            onLoad={() => {
              setTimeout(() => {
                setLoaded(true)
              }, 500)
            }}
          />

          {(isPending || !loaded) && (
            <>
              <div className="absolute inset-0 bg-white" />
              <Skeleton className="absolute inset-0" />
            </>
          )}
        </>
      )}
    </>
  )
}

const className = 'object-contain object-center'
